import type {
  FileProcessResultResponse,
  GetPresignedUrlRequestResponse,
} from '@/api';
import DraggingFileView from '@/components/file-upload/dragging-file-view';
import ProcessingView from '@/components/file-upload/processing-view';
import UploadFinishedView from '@/components/file-upload/upload-finished-view';
import UploadedFileView from '@/components/file-upload/uploaded-file-view';
import {
  type FileUploadResult,
  UploadMode,
  useFileUpload,
  useUppyDropTarget,
} from '@/components/file-upload/use-file-upload';
import { Modal } from '@lego/connect-components-react';
import { Flex } from '@lego/klik-ui';
import { AnimatePresence, motion } from 'framer-motion';
import {
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

type RenderIdleHelpers = {
  onBrowseClick: (e: React.MouseEvent<HTMLElement>) => void;
};

type FileUploadModalProps = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  title: string;
  target?: string;
  getPresignedPutUrl: (
    fileName: string,
  ) => Promise<GetPresignedUrlRequestResponse>;
  contentHeight?: string;
  successMessage?: ReactNode;
  initialFile?: File;
  onClose: () => void;
  onUploadFinished?: (result: FileUploadResult) => void;
  renderIdle: (helpers: RenderIdleHelpers) => ReactNode;
  getReport?: (
    fileId: string,
  ) => Promise<FileProcessResultResponse | undefined>;
  /** Local processing function — skips S3 upload entirely. */
  onLocalProcess?: (file: File) => Promise<FileUploadResult>;
};

const FileUploadModal = ({
  isOpen,
  triggerRef,
  title,
  target,
  getPresignedPutUrl,
  contentHeight = '360px',
  successMessage,
  initialFile,
  onClose,
  onUploadFinished,
  renderIdle,
  getReport,
  onLocalProcess,
}: FileUploadModalProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dropTargetElement, setDropTargetElement] =
    useState<HTMLElement | null>(null);

  const upload = useFileUpload({
    target,
    getPresignedPutUrl,
    getReport,
    onLocalProcess,
    onUploadFinished,
  });
  const {
    mode,
    file,
    uploadProgress,
    uploadStartTime,
    uploadResult,
    errorMessage,
    canUpload,
    setMode,
    processFile,
  } = upload;

  // Reset + seed the flow every time the modal opens.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only run when the modal opens or the seeded file changes
  useEffect(() => {
    if (!isOpen) return;
    upload.reset();
    if (initialFile) {
      processFile(initialFile);
    }
  }, [isOpen, initialFile]);

  useUppyDropTarget({
    element: dropTargetElement,
    enabled: isOpen,
    onFile: processFile,
    onDragStateChange: (isDragging) => {
      setMode((current) => {
        if (isDragging && current === UploadMode.Default) {
          return UploadMode.Dragging;
        }
        if (!isDragging && current === UploadMode.Dragging) {
          return UploadMode.Default;
        }
        return current;
      });
    },
  });

  const handleBrowseClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && files[0]) {
      processFile(files[0]);
    }
    e.target.value = '';
  };

  const handleClose = () => {
    upload.cancelInFlight();
    upload.reset();
    onClose();
  };

  const renderContent = () => {
    switch (mode) {
      case UploadMode.Dragging:
        return <DraggingFileView key="dragging" />;
      case UploadMode.FileDropped:
      case UploadMode.UploadInProgress:
        return (
          file && (
            <UploadedFileView
              key="uploaded"
              file={file}
              uploadProgress={uploadProgress}
              uploadStartTime={uploadStartTime}
              isUploading={mode === UploadMode.UploadInProgress}
              canUpload={canUpload}
              onRemoveFile={upload.reset}
              onUpload={upload.upload}
              onCancel={upload.cancelUpload}
            />
          )
        );
      case UploadMode.Processing:
        return <ProcessingView key="processing" />;
      case UploadMode.UploadFinished:
        return (
          <UploadFinishedView
            key="finished"
            onRetry={upload.reset}
            uploadResult={uploadResult}
            errorMessage={errorMessage}
            successMessage={successMessage}
          />
        );
      case UploadMode.Default:
        return (
          <Flex
            key="default"
            flexDirection="column"
            gap="16px"
            width="100%"
            flex="1"
          >
            {renderIdle({ onBrowseClick: handleBrowseClick })}
          </Flex>
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="medium"
      triggerRef={triggerRef}
    >
      <Modal.Title>{title}</Modal.Title>
      <Modal.Content style={{ padding: '24px' }}>
        <Flex
          ref={setDropTargetElement}
          flexDirection="column"
          height={contentHeight}
          width="100%"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                width: '100%',
              }}
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </Flex>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Modal.Content>
    </Modal>
  );
};

export default FileUploadModal;
